// src/pages/GamExplainerPage.jsx
// Standalone "GAM ad-request decoder ring" — paste a GAM/GPT ad-request URL
// and get a plain-English breakdown of every param, grouped by category,
// plus flags for anything that looks missing (consent, correlator, etc).
// No accounts, no live traffic — just paste and read.
import React, { useMemo, useState } from "react";
import { explainGamUrl } from "../utils/gamParams";

/* ── clipboard helper — same approach as InspectraNetworkView's copyToClipboard,
   reimplemented locally per task instructions (do not import from InspectraNetworkView). ── */
function copyToClipboard(text) {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(String(text));
            return true;
        }
    } catch { /* fall through to legacy path */ }
    try {
        const ta = document.createElement("textarea");
        ta.value = String(text);
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        ta.remove();
        return true;
    } catch { return false; }
}

const CATEGORY_ORDER = [
    ["slot", "Slot"],
    ["targeting", "Targeting"],
    ["consent", "Consent & Privacy"],
    ["sdk/environment", "SDK & Environment"],
    ["video", "Video"],
    ["correlator/session", "Session"],
    ["other", "Other"],
];

const MAX_VALUE_LEN = 80;
function TruncatedValue({ value }) {
    const text = value === undefined || value === null ? "" : String(value);
    if (text.length <= MAX_VALUE_LEN) return <span>{text || "—"}</span>;
    return <span title={text}>{text.slice(0, MAX_VALUE_LEN)}…</span>;
}

function buildMarkdown(result) {
    if (!result || !result.ok) return "";
    const lines = ["| Param | Value | What it means |", "| --- | --- | --- |"];
    for (const [cat, label] of CATEGORY_ORDER) {
        const rows = result.params.filter((p) => p.category === cat);
        if (!rows.length) continue;
        lines.push(`| **${label}** | | |`);
        for (const p of rows) {
            const val = (p.decodedValue ?? p.value ?? "").toString().replace(/\|/g, "\\|");
            lines.push(`| ${p.key} | ${val} | ${p.meaning.replace(/\|/g, "\\|")} |`);
        }
    }
    if (result.custParams.length) {
        lines.push(`| **cust_params** | | |`);
        for (const c of result.custParams) {
            lines.push(`| ${c.key} | ${String(c.value).replace(/\|/g, "\\|")} | ${(c.meaning || "").replace(/\|/g, "\\|")} |`);
        }
    }
    return lines.join("\n");
}

function ParamGroup({ label, rows }) {
    if (!rows.length) return null;
    return (
        <div style={{ display: "grid", gap: 4 }}>
            <div style={s.subHeading}>{label}</div>
            <div style={{ overflowX: "auto" }}>
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={s.th}>Param</th>
                            <th style={s.th}>Value</th>
                            <th style={s.th}>What it means</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((p, i) => (
                            <tr key={p.key + i}>
                                <td style={s.k} title={p.name}>{p.key}</td>
                                <td style={s.v}><TruncatedValue value={p.decodedValue ?? p.value} /></td>
                                <td style={s.v}>{p.meaning}{p.note ? <span style={s.note}> — {p.note}</span> : null}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CustParamsGroup({ rows }) {
    if (!rows.length) return null;
    return (
        <div style={{ display: "grid", gap: 4 }}>
            <div style={s.subHeading}>cust_params (decoded)</div>
            <div style={{ overflowX: "auto" }}>
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={s.th}>Key</th>
                            <th style={s.th}>Value</th>
                            <th style={s.th}>What it means</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((c, i) => (
                            <tr key={c.key + i}>
                                <td style={s.k}>{c.key}</td>
                                <td style={s.v}><TruncatedValue value={c.value} /></td>
                                <td style={s.v}>{c.meaning || <span style={s.muted}>—</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// embedded: rendered as a tab of InspectPage, which owns the paste box and
// passes its text as `value`; this page then drops its own header and input.
export default function GamExplainerPage({ value, embedded = false } = {}) {
    const [localUrl, setUrl] = useState("");
    const url = value ?? localUrl;
    const [copyFeedback, setCopyFeedback] = useState(null);

    const result = useMemo(() => {
        if (!url.trim()) return null;
        try {
            return explainGamUrl(url.trim());
        } catch (e) {
            return { ok: false, reason: e.message, params: [], custParams: [], missing: [] };
        }
    }, [url]);

    const handleCopy = () => {
        const md = buildMarkdown(result);
        const ok = copyToClipboard(md);
        setCopyFeedback(ok ? "Copied" : "Copy failed");
        setTimeout(() => setCopyFeedback(null), 1500);
    };

    return (
        <div style={{ padding: embedded ? "8px 0" : 20, display: "grid", gap: 16, maxWidth: embedded ? "none" : 1100, margin: embedded ? 0 : "0 auto" }}>
            {!embedded && <>
            <div>
                <h2 style={{ margin: 0, color: "var(--ink)" }}>GAM Ad Request Explainer</h2>
                <p style={{ margin: "4px 0 0 0", color: "var(--muted)", fontSize: 13 }}>
                    Paste a GAM/GPT ad-request URL and get a plain-English breakdown of every
                    param, grouped by category, plus flags for anything that looks missing.
                </p>
            </div>

            <div style={{ display: "grid", gap: 6 }}>
                <div style={s.paneLabel}>GAM ad-request URL</div>
                <textarea
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://securepubads.g.doubleclick.net/gampad/ads?iu=...&sz=...&correlator=..."
                    style={s.textarea}
                    spellCheck={false}
                />
            </div>
            </>}

            {!result && <div style={s.hint}>paste a URL to explain it</div>}

            {result && !result.ok && (
                <div style={s.errorHint}>{result.reason || "Could not parse this URL."}</div>
            )}

            {result && result.ok && (
                <div style={{ display: "grid", gap: 16 }}>
                    <div style={s.toolbar}>
                        <span style={s.summary}>
                            {result.host || "(no host)"}
                            {!result.isGamAdRequest && (
                                <span style={s.muted}> — not a recognized GAM ad endpoint, showing best-effort param breakdown</span>
                            )}
                        </span>
                        <button style={s.btn} onClick={handleCopy}>Copy explanation</button>
                        {copyFeedback && <span style={s.copyFeedback}>{copyFeedback}</span>}
                    </div>

                    {result.missing.length > 0 && (
                        <div style={s.warningRow}>
                            {result.missing.map((m, i) => (
                                <span key={m.key + i} style={s.chip} title={m.why}>{m.why}</span>
                            ))}
                        </div>
                    )}

                    {result.params.length === 0 && result.custParams.length === 0 && (
                        <div style={s.hint}>no query params found on this URL</div>
                    )}

                    {CATEGORY_ORDER.map(([cat, label]) => (
                        <ParamGroup
                            key={cat}
                            label={label}
                            rows={result.params.filter((p) => p.category === cat)}
                        />
                    ))}

                    <CustParamsGroup rows={result.custParams} />
                </div>
            )}
        </div>
    );
}

const s = {
    paneLabel: { fontWeight: 600, fontSize: 12, color: "var(--ink)" },
    textarea: {
        minHeight: 90,
        resize: "vertical",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        lineHeight: 1.4,
        padding: 10,
        border: "1px solid var(--line)",
        borderRadius: 8,
        background: "var(--bg)",
        color: "var(--ink)",
        whiteSpace: "pre",
        overflowWrap: "normal",
    },
    toolbar: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
    btn: {
        padding: "6px 10px", fontSize: 12, border: "1px solid var(--line)",
        borderRadius: 8, background: "var(--card)", cursor: "pointer", color: "var(--ink)",
    },
    copyFeedback: { fontSize: 12, color: "var(--muted)" },
    hint: { fontSize: 12, color: "var(--muted)", fontStyle: "italic" },
    errorHint: { fontSize: 12, color: "var(--error-text)" },
    summary: { fontWeight: 600, fontSize: 13, color: "var(--ink)" },
    muted: { fontWeight: 400, color: "var(--muted)" },
    note: { color: "var(--muted)" },
    subHeading: { fontWeight: 600, fontSize: 12, color: "var(--ink)", marginTop: 4 },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 12, color: "var(--ink)" },
    th: { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--line)", color: "var(--muted)" },
    k: {
        width: 200, padding: "6px 8px", borderBottom: "1px solid var(--line)",
        verticalAlign: "top", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere",
        fontFamily: "var(--font-mono)",
    },
    v: { padding: "6px 8px", borderBottom: "1px solid var(--line)", verticalAlign: "top", wordBreak: "break-word" },
    warningRow: { display: "flex", gap: 8, flexWrap: "wrap" },
    chip: {
        display: "inline-block", padding: "4px 10px", borderRadius: 999, fontSize: 12,
        background: "var(--warn-bg, transparent)", color: "var(--warn-text)",
        border: "1px solid var(--warn-text)", maxWidth: 480, overflow: "hidden",
        textOverflow: "ellipsis", whiteSpace: "nowrap",
    },
};
